import { Schema } from "effect";
import { SupportTicketId } from "../../../ids/definitions";
import { ViewColumnsConfig } from "../../../rpc/view-folder";

export const StartSupportTicketExportInput = Schema.Struct({
  /**
   * Encoded SupportTicketFilterGroup string — same format used by
   * listSupportTickets. The export honors exactly the filters applied to the
   * table at click-time. Optional so a user with no active filters can still
   * export (every ticket on the team).
   */
  filterGroup: Schema.optional(Schema.String),
  /**
   * Explicit set of support ticket ids to export. When present and non-empty
   * the export covers exactly these tickets and `filterGroup` is ignored —
   * this is what the "Export CSV" bulk action emits when the user has
   * individually selected rows. Ids the caller can't access (wrong team) are
   * silently dropped, matching the rest of the export's access scoping.
   */
  supportTicketIds: Schema.optional(Schema.Array(SupportTicketId)),
  /**
   * Support ticket ids to drop from a `filterGroup` export. Used by the
   * "select all matching, except these" bulk-selection mode: the filter scopes
   * the set and these ids carve out the unchecked rows. Ignored when
   * `supportTicketIds` is provided.
   */
  excludedSupportTicketIds: Schema.optional(Schema.Array(SupportTicketId)),
  /**
   * Effective column configuration (order + visibility) from the table at the
   * moment the user clicked Export. Same shape persisted on a saved view.
   * `null` / omitted means no customization — server emits the canonical CSV
   * column set in the canonical order. Hidden columns are dropped from the
   * tickets CSV; required identifier/timestamp columns are always emitted.
   */
  columns: Schema.optional(Schema.NullOr(ViewColumnsConfig)),
  /**
   * Display name of the active saved view (e.g. "Unresolved"). Drives the
   * download filename and the email greeting context. Optional — omit when
   * the user has no view selected and the server will fall back to `all`.
   */
  viewName: Schema.optional(Schema.String),
});
export type StartSupportTicketExportInput =
  typeof StartSupportTicketExportInput.Type;

export const StartSupportTicketExportResponse = Schema.Struct({
  workflowId: Schema.String,
  /**
   * Presigned download URL, present only when the export completed within the
   * handler's 30-second inline wait (small exports). The client should start
   * a browser download immediately. When absent the export is still running
   * and the file arrives via the notification email instead.
   */
  fileUrl: Schema.optional(Schema.String),
});
export type StartSupportTicketExportResponse =
  typeof StartSupportTicketExportResponse.Type;
